import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { useAptorApi } from '../../../Api';
import { LawLink } from '../../../Components';
import { TextField } from '../../../Components/Field/TextField';
import { SelectField } from '../../../Components/Field/SelectField';
import { SelectOption, TypedFormFieldValue } from '../../../Components/Form/Form.types';
import { useConfirmation } from '../../../Context/ConfirmationContext/ConfirmationContext';
import { CustomerFeature, UserContext } from '../../../Context/UserContext/UserContext';
import { useIsMounted } from '../../../Hooks/useIsMounted';
import { ILawChange } from '../Features/OurLaws/Law/Changes';
import { IRequirementChange } from '../Features/OtherRequirements/Requirement/Changes';

const PRIMARY = '#c4122f';
const outlinedRedBtn: React.CSSProperties = { fontWeight: 700, borderColor: PRIMARY, color: PRIMARY };

export type ChangeAcknowledgeDialogTarget = {
  id: number;
  number: string;
  name?: string;
  effectiveFrom?: Date;
  userId?: number;
  lawId?: number;
  reqId?: number;
  mode?: 'acknowledge' | 'review' | 'edit';
  acknowledgement?: {
    comment?: string | null;
    affectsBusiness?: boolean | null;
    acknowledgedAt?: Date;
    userName?: string;
  };
};

interface IProps {
  target?: ChangeAcknowledgeDialogTarget | ChangeAcknowledgeDialogTarget[];
  onClose: () => void;
  onAcknowledged: () => void;
  onAcknowledge?: (target: ChangeAcknowledgeDialogTarget, comment: string, affect?: boolean) => Promise<void>;
  onBulkAcknowledge?: (targets: ChangeAcknowledgeDialogTarget[], comment: string, affect?: boolean) => Promise<void>;
}

type AffectsBusiness = true | false | null;

type LoadState = {
  change?: ILawChange | IRequirementChange;
  isLoading: boolean;
};

type FormState = {
  comment: string;
  affectsBusiness: AffectsBusiness;
};

const emptyForm: FormState = { comment: '', affectsBusiness: null };

export const ChangeAcknowledgeDialog = ({ target, onClose, onAcknowledged, onAcknowledge, onBulkAcknowledge }: IProps) => {
  const isBulk = Array.isArray(target);
  const targets = isBulk ? target : target ? [target] : [];
  const single = isBulk ? undefined : target;
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();
  const { deleteConfirmation } = useConfirmation();
  const user = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMounted = useIsMounted();
  const [loadState, setLoadState] = useState<LoadState>({ isLoading: false });
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<FormState>(() => ({
    comment: single?.acknowledgement?.comment || '',
    affectsBusiness: single?.acknowledgement?.affectsBusiness ?? null,
  }));
  const [delegateUsers, setDelegateUsers] = useState<SelectOption[]>([]);
  const [customerUserOptions, setCustomerUserOptions] = useState<SelectOption[]>([]);
  const [delegateOptionsLoaded, setDelegateOptionsLoaded] = useState(false);
  const [delegateMenuPortal, setDelegateMenuPortal] = useState<HTMLDivElement | null>(null);
  const targetUserId = single?.userId ?? user.id;
  const isEditMode = !isBulk && single?.mode === 'edit';
  const isAlreadyAcknowledged =
    Boolean(loadState.change) &&
    Array.isArray((loadState.change as any).unacknowledgedByUsers) &&
    !(loadState.change as any).unacknowledgedByUsers.some((userId: number) => Number(userId) === Number(targetUserId));
  const isReviewMode = !isBulk && !isEditMode && (single?.mode === 'review' || Boolean(single?.acknowledgement) || isAlreadyAcknowledged);
  const userOwnsChange = isBulk ? targets.every(t => Number(t.userId) === Number(user.id)) : (single ? Number(single.userId) === Number(user.id) : false);
  const lawChange = !isBulk && single?.lawId && loadState.change ? (loadState.change as ILawChange) : undefined;
  const requirementChange = !isBulk && single?.reqId && loadState.change ? (loadState.change as IRequirementChange) : undefined;
  const delegatableChange = lawChange ?? requirementChange;
  const isDelegatedUser = delegatableChange?.delegatedUsersIds?.some((userId) => Number(userId) === Number(targetUserId)) ?? false;
  const hasDelegationChanges = user.customerFeatures?.some(
    (feature: any) => (feature.value ?? feature) === CustomerFeature.DelegationChanges,
  );
  const shouldAcknowledge =
    Boolean(lawChange) &&
    Boolean(single?.lawId) &&
    Number(targetUserId) === Number(user.id) &&
    !isReviewMode &&
    (isDelegatedUser || user.complianceUserFor.laws.some((law) => law.id === single?.lawId)) &&
    Boolean(lawChange?.unacknowledgedByUsers?.some((userId) => Number(userId) === Number(targetUserId)));
  const shouldDelegate =
    hasDelegationChanges &&
    Boolean(delegatableChange) &&
    (shouldAcknowledge || (Boolean(requirementChange) && userOwnsChange)) &&
    (delegatableChange?.unacknowledgedByUsers ?? [])
      .filter((userId) => !(delegatableChange?.delegatedUsersIds ?? []).includes(userId))
      .some((userId) => Number(userId) === Number(targetUserId));
  const canEditAcknowledgement = !isReviewMode && (isEditMode ? userOwnsChange : isBulk ? userOwnsChange : single?.reqId ? userOwnsChange : shouldAcknowledge);

  const resetDelegateState = () => {
    setDelegateUsers([]);
    setCustomerUserOptions([]);
    setDelegateOptionsLoaded(false);
  };

  useEffect(() => {
    let cancelled = false;
    resetDelegateState();
    if (!target || Array.isArray(target)) {
      setLoadState({ isLoading: false });
      setIsSaving(false);
      setForm(emptyForm);
      return;
    }
    const loadChange = async () => {
      setForm({ comment: target.acknowledgement?.comment || '', affectsBusiness: target.acknowledgement?.affectsBusiness ?? null });
      setLoadState({ change: undefined, isLoading: true });
      try {
        if (target.lawId) {
          const response = await api.getLawPortalLawChanges<ILawChange>(target.lawId);
          const selectedChange = response.items.find((item) => item.id === target.id || item.number === target.number);
          if (!cancelled) {
            setLoadState({ change: selectedChange, isLoading: true });
          }
        }

        if (target.reqId) {
          const response = await api.getLawPortalRequirementChanges<IRequirementChange>(target.reqId);
          const selectedChange = response.items.find((item) => item.id === target.id || item.name === target.number);
          if (!cancelled) {
            setLoadState({ change: selectedChange, isLoading: true });
          }
        }
      } finally {
        if (!cancelled) {
          setLoadState((prev) => ({ ...prev, isLoading: false }));
        }
      }
    };

    loadChange();
    return () => {
      cancelled = true;
    };
  }, [api, single?.id, single?.lawId, single?.reqId, single?.number]);

  useEffect(() => {
    if (!shouldDelegate || !delegatableChange) {
      return;
    }
    api
      .getCustomerUsers<{ id: number; name: string }>()
      .then((result) => {
        if (!isMounted) {
          return;
        }
        const options = (result?.items || []).map((customerUser) => ({ value: customerUser.id, label: customerUser.name }));
        const responsibleUserIds = (delegatableChange.responsibleUsers ?? []).map((responsibleUser) => responsibleUser.id);
        const eligibleOptions = options.filter((option) => !responsibleUserIds.includes(Number(option.value)));
        setCustomerUserOptions(eligibleOptions);
        setDelegateUsers(
          delegatableChange.delegatedUsersIds?.length
            ? options.filter((option) => delegatableChange.delegatedUsersIds!.includes(Number(option.value)))
            : [],
        );
        setDelegateOptionsLoaded(true);
      })
      .catch((error) => {
        if (isMounted) {
          console.error('Failed to load customer users:', error);
          setDelegateOptionsLoaded(true);
        }
      });
  }, [api, isMounted, delegatableChange, shouldDelegate]);

  const handleClose = () => {
    setLoadState({ isLoading: false });
    setIsSaving(false);
    setForm(emptyForm);
    resetDelegateState();
    onClose();
  };

  const acknowledgeAction = async () => {
    if (isSaving || targets.length === 0) {
      return;
    }
    setIsSaving(true);
    const affectValue = form.affectsBusiness === null ? undefined : form.affectsBusiness;
    try {
      if (isBulk) {
        await onBulkAcknowledge?.(targets, form.comment, affectValue);
      } else if (loadState.change && single) {
        await onAcknowledge?.({ ...single, id: loadState.change.id }, form.comment, affectValue);
      }

      handleClose();
      onAcknowledged();
    } finally {
      if (isMounted) {
        setIsSaving(false);
      }
    }
  };

  const handleAcknowledge = () => {
    if (!canEditAcknowledgement) {
      return;
    }

    deleteConfirmation(acknowledgeAction, {
      title: formatMessage({ id: 'law-portal.our-law.ack.several' }),
      description: formatMessage({ id: 'law-portal.our-law.ack.several.confirmation' }),
    });
  };

  const handleDelegateChange = useCallback(
    (val: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
      setDelegateUsers(Array.isArray(val) ? (val as SelectOption[]) : val ? [val as SelectOption] : []);
    },
    [],
  );
  const handleCommentChange = useCallback((val: TypedFormFieldValue<string> | undefined) => {
    const comment = val?.value || '';
    setForm((prev) => (prev.comment === comment ? prev : { ...prev, comment }));
  }, []);

  const delegateAction = async () => {
    if ((!single?.lawId && !single?.reqId) || !delegatableChange || isSaving) {
      return;
    }
    setIsSaving(true);
    const userIds = delegateUsers.map((delegateUser) => Number(delegateUser.value));
    try {
      if (single.lawId) {
        await api.updateLawChangeDelegatedUsers(single.lawId, delegatableChange.id, userIds);
      } else if (single.reqId) {
        await api.updateRequirementChangeDelegatedUsers(single.reqId, delegatableChange.id, userIds);
      }
      if (isMounted) {
        setLoadState((prev) => ({
          ...prev,
          change: prev.change ? ({ ...prev.change, delegatedUsersIds: userIds } as ILawChange | IRequirementChange) : prev.change,
        }));
      }
      onAcknowledged();
    } finally {
      if (isMounted) {
        setIsSaving(false);
      }
    }
  };

  const handleDelegate = () => {
    if (!shouldDelegate) {
      return;
    }
    deleteConfirmation(delegateAction, {
      title: formatMessage({ id: 'component.law-change.acknowledge.label' }),
      description: formatMessage({ id: 'component.law-change.delegate.confirmation' }),
    });
  };
  const renderChangeDetails = () => {
    const change = loadState.change;
    if (!change) {
      return null;
    }
    const lawChange = change as ILawChange;
    return (
      <Box>
        <Typography dangerouslySetInnerHTML={{ __html: change.shortDescription }} />
        {'extent' in change && lawChange.extent && <Typography>{lawChange.extent}</Typography>}
        {'urlAddress' in change && lawChange.urlAddress && <LawLink type="law-change" url={lawChange.urlAddress} />}
        <Typography>
          {single?.reqId ? (
            <FormattedMessage
              id="law-portal.requirements.changes.effectiveFrom"
              values={{ date: moment(change.effectiveFrom).format('ll') }}
            />
          ) : (
            <FormattedMessage
              id="component.law-change.effectiveFrom"
              values={{ date: moment(change.effectiveFrom).format('ll') }}
            />
          )}
        </Typography>
        {'hasTransitionalRegulation' in change && lawChange.hasTransitionalRegulation === true && (
          <Typography>
            <FormattedMessage id="component.law-change.has-transitional-regulation" />
          </Typography>
        )}
        {'reprint' in change && lawChange.reprint && (
          <Typography>
            <FormattedMessage id="component.law-change.reprint" values={{ reprint: lawChange.reprint }} />
          </Typography>
        )}
        {'includeInSendouts' in change && (
          <Typography>
            <FormattedMessage
              id={
                lawChange.includeInSendouts
                  ? 'component.law-change.included-in-sendouts'
                  : 'component.law-change.not-included-in-sendouts'
              }
            />
          </Typography>
        )}
      </Box>
    );
  };

  const renderBulkList = () => (
    <Box style={{ marginBottom: theme.spacing(2) }}>
      <Typography>{formatMessage({ id: 'law-portal.our-law.ack.several.title' }, { count: targets.length })}</Typography>
      {targets.map((t) => (
        <Typography key={`${t.id}-${t.number}`} style={{ marginTop: theme.spacing(1) }}>
          {t.name} — {t.number}
          {t.effectiveFrom && (
            <>
              {' — '}
              <FormattedMessage id="component.law-change.effectiveFrom" values={{ date: moment(t.effectiveFrom).format('ll') }} />
            </>
          )}
        </Typography>
      ))}
    </Box>
  );

  const renderAcknowledgementInfo = () => {
    if (!isReviewMode || !single?.acknowledgement) {
      return null;
    }

    return (
      <Box style={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(1) }}>
        {single.acknowledgement.userName && (
          <Typography>
            {formatMessage({ id: 'law-portal.statistics.table.user' })}: {single.acknowledgement.userName}
          </Typography>
        )}
        {single.acknowledgement.acknowledgedAt && (
          <Typography>
            {formatMessage({ id: 'law-portal.statistics.table.acknowledgedAt' })}: {moment(single.acknowledgement.acknowledgedAt).format('ll')}
          </Typography>
        )}
        {form.comment && (
          <Box style={{ marginTop: theme.spacing(2) }}>
            <Typography>{formatMessage({ id: 'component.law-change.acknowledge.comment' })}</Typography>
            <Typography style={{ whiteSpace: 'pre-wrap' }}>{form.comment}</Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderAcknowledgeForm = () => (
    <Box style={{ marginTop: theme.spacing(3) }}>
      <Box style={{ marginBottom: theme.spacing(3), display: 'flex', flexWrap: 'wrap', gap: isMobile ? '8px' : '16px' }}>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={form.affectsBusiness === true}
            name="affect"
            disabled={isReviewMode}
            onChange={(_, value) => setForm((prev) => ({ ...prev, affectsBusiness: value ? true : null }))}
            inputProps={{ 'aria-label': 'primary checkbox' }}
          />
          <label>{formatMessage({ id: 'law-portal.our-law.ack.latest-change.checkbox.text.affect' })}</label>
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={form.affectsBusiness === false}
            name="not_affect"
            disabled={isReviewMode}
            onChange={(_, value) => setForm((prev) => ({ ...prev, affectsBusiness: value ? false : null }))}
            inputProps={{ 'aria-label': 'primary checkbox' }}
          />
          <label>{formatMessage({ id: 'law-portal.our-law.ack.latest-change.checkbox.text.not_affect' })}</label>
        </Box>
      </Box>
    </Box>
  );

  const renderCommentForm = () => {
    const commentLabel = formatMessage({ id: 'component.law-change.acknowledge.comment' });

    if (isReviewMode) {
      return (
        <Box style={{ marginTop: theme.spacing(2), maxWidth: '640px' }}>
          <Typography>{commentLabel}</Typography>
          <Box
            style={{
              minHeight: '34px',
              padding: '8px 10px',
              border: '1px solid #bdbdbd',
              backgroundColor: '#fff',
              boxSizing: 'border-box',
              whiteSpace: 'pre-wrap',
            }}
          >
            {form.comment}
          </Box>
        </Box>
      );
    }

    return (
      <Box style={{ marginTop: theme.spacing(2) }}>
        <Box style={{ maxWidth: '640px' }}>
          <TextField
            key={isBulk ? `bulk-${targets.length}` : `${single?.id}-edit`}
            label={commentLabel}
            name="comment"
            errorTexts={[]}
            initialState={form.comment ? { value: form.comment, isValid: true } : undefined}
            onChange={handleCommentChange}
          />
        </Box>
        {shouldDelegate && delegateOptionsLoaded && (
          <Box style={{ maxWidth: '640px', marginTop: theme.spacing(2) }}>
            <SelectField
              label={formatMessage({ id: 'component.law-change.delegate.label' })}
              name="delegateUsers"
              options={customerUserOptions}
              isMulti={true}
              errorTexts={[]}
              initialState={delegateUsers}
              onChange={handleDelegateChange}
              classNamePrefix="delegate-select"
              menuPortalTarget={delegateMenuPortal ?? undefined}
              menuPosition="fixed"
              menuPlacement="auto"
              maxMenuHeight={220}
              menuShouldScrollIntoView={false}
            />
          </Box>
        )}
      </Box>
    );
  };

  const isAcknowledgeDisabled = isBulk
    ? targets.length === 0 || isSaving
    : !loadState.change || loadState.isLoading || isSaving || !canEditAcknowledgement;

  const renderActionButton = () => {
    return (
      <Button
        variant="outlined"
        onClick={handleAcknowledge}
        disabled={isAcknowledgeDisabled}
        style={{
          ...outlinedRedBtn,
          color: isAcknowledgeDisabled ? '#999' : PRIMARY,
          minWidth: '140px',
        }}
      >
        {formatMessage({ id: 'component.law-change.acknowledge.button' })}
      </Button>
    );
  };

  return (
    <Dialog open={isBulk ? targets.length > 0 : Boolean(target)} maxWidth="lg" fullWidth onClose={handleClose}>
      <DialogTitle style={{ borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" component="span">
          {isBulk
            ? formatMessage({ id: 'law-portal.our-law.ack.several' })
            : formatMessage({ id: 'component.law-change.acknowledge.button' })}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ padding: isMobile ? theme.spacing(2) : theme.spacing(3), position: 'relative' }}>
        <div ref={setDelegateMenuPortal} />
        <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="outlined" onClick={handleClose} style={outlinedRedBtn}>
            {formatMessage({ id: 'form.cancel' })}
          </Button>
        </Box>

        <Box style={{ minHeight: '420px', display: 'flex', flexDirection: 'column' }}>
          {(loadState.isLoading || isSaving) && (
            <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10 }}>
              <CircularProgress />
            </Box>
          )}

          <Box
            style={{
              width: '100%',
              padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
              backgroundColor: '#f7f0f1',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              minHeight: '360px',
              maxHeight: isMobile ? '68vh' : '62vh',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              style={{
                width: '100%',
                maxHeight: isMobile ? '64vh' : '58vh',
                overflow: 'auto',
                backgroundColor: '#f7f0f1',
                padding: isMobile ? `${theme.spacing(1)}px ${theme.spacing(2)}px` : `${theme.spacing(1)}px ${theme.spacing(3)}px`,
                boxSizing: 'border-box',
              }}
            >
              <Box style={{ maxWidth: '960px', width: '100%' }}>
                {!isBulk && single?.number && (
                  <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: theme.spacing(1) }}>
                    {single.number}
                  </Typography>
                )}
                {isBulk && renderBulkList()}
                {renderChangeDetails()}
                {renderAcknowledgementInfo()}
                {canEditAcknowledgement && renderAcknowledgeForm()}
                {canEditAcknowledgement && renderCommentForm()}
              </Box>
            </Box>
          </Box>

          <Box style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            {canEditAcknowledgement &&
              (isSaving ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  {shouldDelegate && (
                    <Button
                      variant="outlined"
                      onClick={handleDelegate}
                      disabled={!delegateOptionsLoaded}
                      startIcon={<Icon>check</Icon>}
                      style={outlinedRedBtn}
                    >
                      {formatMessage({ id: 'component.law-change.delegate.button' })}
                    </Button>
                  )}
                  {renderActionButton()}
                </>
              ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
